const initialState = () => ({
  searchFilters: {},
  status: {
    error: null,
    pending: true,
    resolved: false,
    rejected: false,
  },
});

const state = initialState();

const actions = {
  resetExploreSearchFilters({ commit }) {
    commit('RESET_SEARCH_FILTERS');
  },
  setPlatforms({ commit }, platforms) {
    commit('SET_PLATFORMS', platforms);
  },
  setAdministrativeRegions({ commit }, { platform, regions }) {
    commit('SET_ADMINISTRATIVE_REGIONS', { platform, regions });
  },
  setClassifications({ commit }, { classifications }) {
    commit('SET_CLASSIFICATIONS', { classifications });
  },
  setReadFilters({ commit }, readFilters) {
    commit('SET_READ_FILTERS', readFilters);
  },
  setShareFilters({ commit }, shareFilters) {
    commit('SET_SHARE_FILTERS', shareFilters);
  },
  setFollowFilters({ commit }, followFilters) {
    commit('SET_FOLLOW_FILTERS', followFilters);
  },
  setStatuses({ commit }, statuses) {
    commit('SET_STATUSES', statuses);
  },
  setClosingBefore({ commit }, closingBefore) {
    commit('SET_CLOSING_BEFORE', closingBefore);
  },
  setPublicationDates({ commit }, publicationDates) {
    commit('SET_PUBLICATION_DATES', publicationDates);
  },
  setAssigneesFilters({ commit }, assigneesFilters) {
    commit('SET_ASSIGNEES', assigneesFilters);
  },
  setKeywords({ commit }, { keywords }) {
    commit('SET_KEYWORDS', keywords);
  },
  setAdvancedSearchFilters({ commit }, { keywordIds, matchContexts, procurementCategories, zones }) {
    commit('SAVE_ADVANCED_SEARCH_FILTERS', { keywordIds, matchContexts, procurementCategories, zones });
  },
};

const mutations = {
  RESET_SEARCH_FILTERS(state) {
    state.searchFilters = {
      administrativeRegions: {},
      classifications: [],
      assigneesFilters: [],
      keywords: null,
      platforms: [],
      statuses: [],
      readFilters: [],
      shareFilters: [],
      followFilters: [],
      closingBefore: null,
      procurementCategories: [],
      zones: {},
      keywordIds: [],
      matchContexts: [],
      // Pre-declared so SET_PUBLICATION_DATES mutates existing (reactive) keys rather than adding
      // new ones — Vue 2 can't observe properties added after the object is made reactive.
      publishedAfter: null,
      publishedBefore: null,
    };
  },
  SET_SEARCH_FILTERS(state, searchFilters) {
    state.searchFilters = searchFilters;
  },
  SET_PLATFORMS(state, platforms) {
    state.searchFilters.platforms = platforms;
  },
  SET_ADMINISTRATIVE_REGIONS(state, { platform, regions }) {
    // Accept regions as an array and convert to object format for storage
    if (!state.searchFilters.administrativeRegions || typeof state.searchFilters.administrativeRegions !== 'object') {
      state.searchFilters.administrativeRegions = {};
    }
    // If regions is an array, store as { platform: regions }
    if (Array.isArray(regions)) {
      state.searchFilters.administrativeRegions[platform] = regions;
    } else {
      // If regions is already an object, merge
      Object.assign(state.searchFilters.administrativeRegions, regions);
    }
  },
  SET_CLASSIFICATIONS(state, { classifications }) {
    state.searchFilters.classifications = classifications;
  },
  SET_STATUSES(state, statuses) {
    state.searchFilters.statuses = statuses;
  },
  SET_READ_FILTERS(state, readFilters) {
    state.searchFilters.readFilters = readFilters;
  },
  SET_SHARE_FILTERS(state, shareFilters) {
    state.searchFilters.shareFilters = shareFilters;
  },
  SET_FOLLOW_FILTERS(state, followFilters) {
    state.searchFilters.followFilters = followFilters;
  },
  SET_CLOSING_BEFORE(state, closingBefore) {
    state.searchFilters.closingBefore = closingBefore;
  },
  SET_PUBLICATION_DATES(state, publicationDates) {
    state.searchFilters.publishedAfter = publicationDates.publishedAfter;
    state.searchFilters.publishedBefore = publicationDates.publishedBefore;
  },
  SET_ASSIGNEES(state, assigneesFilters) {
    state.searchFilters.assigneesFilters = assigneesFilters;
  },
  SET_KEYWORDS(state, keywords) {
    state.searchFilters.keywords = keywords;
  },
  SAVE_ADVANCED_SEARCH_FILTERS(state, { keywordIds, matchContexts, procurementCategories, zones }) {
    state.searchFilters.keywordIds = keywordIds;
    state.searchFilters.matchContexts = matchContexts;
    state.searchFilters.procurementCategories = procurementCategories;
    state.searchFilters.zones = zones;
  },
};

const getters = {
  searchFilters: (state) => state.searchFilters || {},

  // filters
  assigneesFilters: (state) => state.searchFilters.assigneesFilters || [],
  closingBefore: (state) => state.searchFilters.closingBefore || null,
  followFilters: (state) => state.searchFilters.followFilters || {},
  matchContexts: (state) => state.searchFilters.matchContexts || [],
  keywords: (state) => state.searchFilters.keywords || null,
  keywordIds: (state) => state.searchFilters.keywordIds || [],
  platforms: (state) => state.searchFilters.platforms || [],
  administrativeRegions: (state) => state.searchFilters.administrativeRegions || {},
  procurementCategories: (state) => state.searchFilters.procurementCategories || [],
  readFilters: (state) => state.searchFilters.readFilters || {},
  shareFilters: (state) => state.searchFilters.shareFilters || {},
  statuses: (state) => state.searchFilters.statuses || [],
  zones: (state) => state.searchFilters.zones || {},
  classificationFilters: (state) => state.searchFilters.classifications || [],

  // advanced search
  hasActiveFilters: (state) => {
    const filters = state.searchFilters || {};
    // List of keys to check
    const filterKeys = [
      'assigneesFilters',
      'closingBefore',
      'followFilters',
      'matchContexts',
      'keywords',
      'keywordIds',
      'platforms',
      'procurementCategories',
      'readFilters',
      'shareFilters',
      'statuses',
      'zones',
      'publishedAfter',
      'publishedBefore',
    ];
    return filterKeys.some((key) => {
      const value = filters[key];
      if (Array.isArray(value)) {
        return value.length > 0;
      }
      if (value && typeof value === 'object') {
        return Object.keys(value).length > 0;
      }
      return value !== null && value !== undefined && value !== '';
    });
  },

  // Ephemeral filters
  publishedAfter: (state) => state.searchFilters.publishedAfter || null,
  publishedBefore: (state) => state.searchFilters.publishedBefore || null,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
};

export const exploreSearchFilters = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters,
};
